import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import uitoolkit from '@zoom/videosdk-ui-toolkit';
import '@zoom/videosdk-ui-toolkit/dist/videosdk-ui-toolkit.css';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { useApp } from '../context/AppContext';
import { useMutation } from '@tanstack/react-query';
import { postFeedback } from '../services/FeedbackService';
import { FeedbackFormSheet } from './new/components/bookings/FeedbackFormSheet';
import { SuccessModal } from '../components/modals/SuccessModal';
import { ErrorModal } from './new/components/error/ErrorModal';

export const VideoCall: React.FC = () => {
  const location = useLocation();
  const {
    sessionTopic,
    videoJwt,
    username,
    bookingId,
    professionalId,
    userFeedbackGiven,
    professionalFeedbackGiven,
    accepted,
    isCancelled,
    paymentStatus,
    rate,
  } = location.state || {};
  const { profile, token } = useApp();
  const [sessionEnded, setSessionEnded] = useState(false);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [joinedAt, setJoinedAt] = useState<number | null>(null);
  const [showFeedbackSheet, setShowFeedbackSheet] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');

  const closeAndResetFeedbackForm = () => {
    setShowFeedbackSheet(false);
    setRating(0);
    setComment('');
  };

  const config = {
    sessionName: sessionTopic,
    userName: username,
    videoSDKJWT: videoJwt,
    features: ['video', 'audio', 'settings', 'users', 'chat', 'share'],
    options: { init: {}, audio: {}, video: {}, share: {} },
    virtualBackground: {
      allowVirtualBackground: true,
      allowVirtualBackgroundUpload: true,
      virtualBackgrounds: [
        'https://images.unsplash.com/photo-1715490187538-30a365fa05bd?q=80&w=1945&auto=format&fit=crop',
      ],
    },
  };

  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('Window beforeunload event triggered');
      sessionClosed();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log('Cleaning up beforeunload event listener');
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    console.log('Session started effect triggered');
    setSessionStarted(true);
  }, []);

  useEffect(() => {
    if (sessionStarted) {
      joinSession();
    }
  }, [sessionStarted]);

  function joinSession() {
    setSessionEnded(false);
    const sessionContainer = document.getElementById('sessionContainer');
    if (sessionContainer) {
      console.log('Joining session with config:', config);
      uitoolkit.joinSession(sessionContainer, config);
      uitoolkit.onSessionJoined(sessionJoined);
      uitoolkit.onSessionClosed(sessionClosed);
    } else {
      console.error('Session container not found');
    }
  }

  const sessionJoined = () => {
    console.log('Session joined');
    setJoinedAt(Date.now());
  };

  const hasCurrentUserFeedback = () => {
    if (!profile?.id || !professionalId) {
      return false;
    }

    return profile.id === professionalId
      ? Boolean(professionalFeedbackGiven)
      : Boolean(userFeedbackGiven);
  };

  const shouldShowCallFeedback = () => {
    if (!joinedAt || !bookingId || hasCurrentUserFeedback()) {
      return false;
    }

    const isPaidOrFree = Number(rate ?? 0) === 0 || paymentStatus === 'succeeded';
    if (!accepted || isCancelled || !isPaidOrFree) {
      return false;
    }

    const callDurationMs = Date.now() - joinedAt;
    return callDurationMs > 10 * 60 * 1000;
  };

  const sessionClosed = () => {
    const sessionContainer = document.getElementById('sessionContainer');
    console.log('Session closed');
    if (sessionContainer) {
      uitoolkit.closeSession(sessionContainer);
    }
    setShowFeedbackSheet(shouldShowCallFeedback());
    setSessionEnded(true);
    setSessionStarted(false);
  };

  const isCommentValid = comment.length <= 500;
  const canSubmit = rating >= 1 && rating <= 5 && isCommentValid;

  const {
    mutate: submitFeedback,
    isPending,
    isError,
    reset,
  } = useMutation({
    mutationFn: async () => {
      if (!bookingId) {
        throw new Error('Missing bookingId for feedback submission.');
      }

      await postFeedback(
        {
          bookingId,
          rating,
          comment: comment.trim(),
        },
        token || ''
      );
    },
    onSuccess: () => {
      setShowFeedbackSheet(false);
      setShowSuccess(true);
      setRating(0);
      setComment('');
    },
    onError: (error) => {
      setShowFeedbackSheet(false);
      console.error('Error submitting call feedback:', error);
    },
  });

  return (
    <NavigationWrapper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#f5f5f5',
          paddingTop: 2,
        }}
      >
        <Box
          sx={{
            width: '90%',
            maxWidth: '1000px',
            height: '700px',
            backgroundColor: 'white',
            padding: 2,
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          {sessionStarted && (
            <div
              id="sessionContainer"
              style={{ height: '600px', width: '100%' }}
            ></div>
          )}
          {sessionEnded && (
            <Typography
              variant="h6"
              sx={{ textAlign: 'center', marginTop: 2, color: 'red' }}
            >
              Session Ended
            </Typography>
          )}
        </Box>
      </Box>

      <ErrorModal
        isInError={isError}
        errorMessage="An error has occurred. Please try again."
        dismissAction={() => reset()}
        retryAction={() => submitFeedback()}
        isLoading={isPending}
        testId="zoom-call-feedback-error-modal"
      />

      <SuccessModal
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Thank you"
        message="Thank you for your feedback"
      />

      <FeedbackFormSheet
        opened={showFeedbackSheet}
        onClose={closeAndResetFeedbackForm}
        rating={rating}
        onRatingChange={setRating}
        comment={comment}
        onCommentChange={setComment}
        isSubmitting={isPending}
        canSubmit={canSubmit}
        onSubmit={() => submitFeedback()}
        testIdPrefix="zoom-call-feedback"
      />
    </NavigationWrapper>
  );
};

export default VideoCall;
